import {
  EmployerSummaryFragment,
  useCreateWorkLocationAssignment,
  useDeleteWorkLocationAssignment,
} from '@alice-financial/alice-sdk'
import { Checkbox } from '@alice-financial/pretext-ui'
import React from 'react'
import { useOrg } from '../organization/useOrg'

type PaygroupAssignmentCheckboxesProps = {
  workLocationId: number
  selectedPayGroups: Array<Pick<EmployerSummaryFragment, 'id' | 'name'>> | null
  availablePayGroups: Array<EmployerSummaryFragment>
}

export const PaygroupAssignmentCheckboxes: React.FC<PaygroupAssignmentCheckboxesProps> = ({
  workLocationId,
  availablePayGroups,
  selectedPayGroups,
}) => {
  const org = useOrg()
  selectedPayGroups = selectedPayGroups || []
  // State for optimistic updates - tracks which paygroups have pending changes
  const [optimisticUpdates, setOptimisticUpdates] = React.useState<Record<number, boolean>>({})

  // Clear optimistic updates when selectedPayGroups changes (e.g., after successful query refetch)
  React.useEffect(() => {
    // Clear any optimistic updates that now match the server state
    setOptimisticUpdates((prev) => {
      const next = { ...prev }
      let hasChanges = false

      Object.keys(next).forEach((paygroupIdStr) => {
        const paygroupId = parseInt(paygroupIdStr, 10)
        const optimisticValue = next[paygroupId]
        const serverValue = selectedPayGroups.some((selected) => selected.id === paygroupId)

        // If optimistic state matches server state, clear it
        if (optimisticValue === serverValue) {
          delete next[paygroupId]
          hasChanges = true
        }
      })

      return hasChanges ? next : prev
    })
  }, [selectedPayGroups])

  // Set up the mutation hooks with optimistic update handling
  const { mutate: createAssignment, isLoading: isCreating } = useCreateWorkLocationAssignment(org?.id || 0, {
    onError: (error, variables) => {
      console.error('Failed to create work location assignment:', error)
      // Revert optimistic state on error
      const employerId = variables.input.employerId
      if (employerId != null) {
        setOptimisticUpdates((prev) => {
          const next = { ...prev }
          delete next[employerId]
          return next
        })
      }
    },
  })

  const { mutate: deleteAssignment, isLoading: isDeleting } = useDeleteWorkLocationAssignment(org?.id || 0, {
    onError: (error, variables) => {
      console.error('Failed to delete work location assignment:', error)
      // Revert optimistic state on error
      const employerId = variables.input.employerId
      if (employerId != null) {
        setOptimisticUpdates((prev) => {
          const next = { ...prev }
          delete next[employerId]
          return next
        })
      }
    },
  })

  const isLoading = isCreating || isDeleting

  // Check if a paygroup is currently selected (with optimistic updates)
  const isPaygroupSelected = (paygroupId: number) => {
    // If there's an optimistic update for this paygroup, use that
    if (paygroupId in optimisticUpdates) {
      return optimisticUpdates[paygroupId]
    }
    // Otherwise, use the actual server state
    return selectedPayGroups.some((selected) => selected.id === paygroupId)
  }

  // Handle checkbox change
  const handlePaygroupChange = (paygroupId: number, isChecked: boolean) => {
    // Immediately set optimistic state for instant UI feedback
    setOptimisticUpdates((prev) => ({
      ...prev,
      [paygroupId]: isChecked,
    }))

    if (isChecked) {
      // Create assignment
      createAssignment({
        input: {
          workLocationId,
          employerId: paygroupId,
        },
      })
    } else {
      // Delete assignment
      deleteAssignment({
        input: {
          workLocationId,
          employerId: paygroupId,
        },
      })
    }
  }

  if (availablePayGroups.length === 0) {
    return null
  }

  return (
    <>
      {availablePayGroups.map((payGroup) => (
        <Checkbox
          key={payGroup.id}
          checked={isPaygroupSelected(payGroup.id)}
          onChange={(event) => handlePaygroupChange(payGroup.id, event.target.checked)}
          disabled={isLoading}
          label={`${payGroup.name} (${payGroup.marketingFriendlyName})`}
        />
      ))}
    </>
  )
}
